import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Loader2, Play, Pause, RefreshCw } from 'lucide-react';

const VIEWS = [
  { key: 'athlete', label: 'Athlete', desc: 'Cinematic athlete rep' },
  { key: 'anatomy', label: 'Anatomy', desc: 'Muscles worked highlighted' },
];

const VIDEO_SRC = '/squat/squat-athlete';
const ANATOMY_IMG = '/exercise-anatomy/squat.png';

const AthleteVideo = () => {
  const ref = useRef(null);
  const [status, setStatus] = useState('loading');
  const [playing, setPlaying] = useState(true);

  const probe = () => {
    setStatus('loading');
    fetch(`${VIDEO_SRC}.mp4`, { method: 'HEAD', cache: 'no-store' })
      .then((r) => {
        const ct = r.headers.get('content-type') || '';
        setStatus(r.ok && ct.startsWith('video/') ? 'ready' : 'error');
      })
      .catch(() => setStatus('error'));
  };

  useEffect(() => {
    probe();
    const id = setInterval(() => { if (status !== 'ready') probe(); }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <div
      className="w-full rounded-2xl overflow-hidden border border-primary/25 bg-black relative flex items-center justify-center"
      style={{ height: 520 }}
      data-testid="squat-athlete-video"
    >
      {status === 'loading' && (
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-3" />
          <p className="text-primary font-heading uppercase tracking-widest text-xs">Rendering</p>
          <p className="text-muted-foreground text-xs mt-1">Video will appear when ready</p>
        </div>
      )}
      {status === 'error' && (
        <div className="text-center px-6">
          <p className="text-red-400 font-heading uppercase tracking-widest text-xs mb-2">Video not ready</p>
          <button
            onClick={probe}
            className="inline-flex items-center gap-1.5 bg-primary/20 hover:bg-primary/30 border border-primary/40 px-3 py-1.5 rounded-lg text-primary text-xs uppercase tracking-widest font-heading"
            data-testid="video-retry-btn"
          >
            <RefreshCw className="h-3 w-3" />Retry
          </button>
        </div>
      )}
      {status === 'ready' && (
        <>
          <video
            key={VIDEO_SRC}
            ref={ref}
            className="w-full h-full object-contain"
            autoPlay loop muted playsInline preload="auto"
            data-testid="squat-video-player"
          >
            <source src={`${VIDEO_SRC}.webm`} type="video/webm" />
            <source src={`${VIDEO_SRC}.mp4`} type="video/mp4" />
          </video>
          <button
            onClick={toggle}
            className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-primary text-black flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
            data-testid="video-play-toggle"
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
          </button>
        </>
      )}
    </div>
  );
};

const AnatomyPanel = () => (
  <div
    className="w-full rounded-2xl overflow-hidden border border-primary/25 bg-neutral-100 flex items-center justify-center"
    style={{ height: 520 }}
    data-testid="squat-anatomy-view"
  >
    <img
      src={ANATOMY_IMG}
      alt="Muscle groups worked during a squat"
      className="w-full h-full object-contain"
      data-testid="squat-anatomy-img"
    />
  </div>
);

export const SquatDemo3DPage = () => {
  const [view, setView] = useState('athlete');
  const current = VIEWS.find((v) => v.key === view);

  return (
    <div className="min-h-screen bg-black text-white pb-16">
      <div className="px-4 pt-4 pb-6 max-w-3xl mx-auto">
        <Link
          to="/exercises"
          className="inline-flex items-center gap-1.5 text-primary/80 hover:text-primary text-sm font-heading uppercase tracking-wider mb-4"
          data-testid="squat-demo-3d-back"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-wider mb-1">
          Squat · Motion Preview
        </h1>
        <p className="text-sm text-muted-foreground mb-4">{current?.desc}</p>

        {/* View toggle */}
        <div className="flex gap-2 mb-4" data-testid="view-toggle">
          {VIEWS.map((v) => {
            const active = v.key === view;
            return (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`flex-1 px-3 py-2 rounded-xl border font-heading uppercase tracking-wider text-xs transition-colors ${
                  active
                    ? 'bg-primary text-black border-primary'
                    : 'bg-black/40 text-primary/80 border-primary/25 hover:border-primary/60'
                }`}
                data-testid={`view-toggle-${v.key}`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        {view === 'athlete' ? <AthleteVideo /> : <AnatomyPanel />}

        <div className="mt-5 bg-card border border-border rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-primary/70 font-heading mb-2">Primary Muscles</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {['Quadriceps', 'Glutes', 'Hamstrings', 'Calves', 'Core', 'Lower Back'].map((m) => (
              <div
                key={m}
                className="bg-primary/15 border border-primary/40 rounded-lg px-3 py-2 text-center"
                data-testid={`muscle-${m.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="text-sm font-heading uppercase tracking-wider text-primary">{m}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-widest text-primary/70 font-heading mb-1">Focus</p>
            <p className="text-sm text-white">Quads · Glutes · Core</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-widest text-primary/70 font-heading mb-1">Tempo</p>
            <p className="text-sm text-white">2s down · 2s up</p>
          </div>
        </div>

        <div className="mt-4 bg-card border border-border rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-primary/70 font-heading mb-2">Cues</p>
          <ul className="text-sm text-white/85 space-y-1.5 list-disc pl-4">
            <li>Feet shoulder-width, toes slightly out</li>
            <li>Sit back into the hips before bending the knees</li>
            <li>Keep chest tall, spine neutral</li>
            <li>Drive through mid-foot on the way up</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SquatDemo3DPage;
